import { useContext } from 'react'
import { ShareVersionContext } from 'modules/versioning'
import {
  PresentationFileFragment,
  PresentationFileFragmentDoc,
  useGetPresentationAssetsByArtboardQuery,
  usePresentationFileSubscription,
} from '@sketch/gql-types'
// eslint-disable-next-line no-restricted-imports
import { RenderStatusType } from '@sketch/gql-types/expansive'
import { dataIdFromObject } from '@sketch/graphql-cache'

interface UsePresentationFileProps {
  artboardUuid: string
}

/**
 * When we arrive on a prototype we just have the start Artboard UUID. However
 * presentation file assets for the whole doc are currently accessed via the
 * Page type. So we have to go via the Artboard's page to get the presentation
 * file info.
 */
export const usePresentationFile = ({
  artboardUuid,
}: UsePresentationFileProps) => {
  const shareVersionContext = useContext(ShareVersionContext)

  const renderStatus = !shareVersionContext?.loading
    ? shareVersionContext?.currentVersion.document?.renderStatus ?? null
    : null

  const documentId = shareVersionContext?.loading
    ? null
    : shareVersionContext?.currentVersion.document?.identifier ?? null

  const prFileQuery = useGetPresentationAssetsByArtboardQuery({
    variables: {
      artboardUuid: artboardUuid,
      documentId: documentId || '',
    },
    skip: renderStatus !== 'FINISHED',
  })

  const page = prFileQuery.data?.artboardInDocumentWithUuid?.page
  const pageIdentifier = page?.identifier
  const presentationFile = page?.presentationFile

  const isLoading = shareVersionContext?.loading || prFileQuery.loading
  const isRendering =
    renderStatus !== 'FINISHED' || presentationFile?.status === 'PENDING'
  const hasError = prFileQuery.error
  const hasNotFoundError = prFileQuery.error?.graphQLErrors.some(
    x => x.extensions?.code === 'NOT_FOUND'
  )

  usePrFileStatusChangesSubscriptionIfNeeded({
    renderStatus,
    presentationFile,
    pageIdentifier,
  })

  return {
    page,
    isLoading,
    isRendering,
    hasError,
    hasNotFoundError,
  }
}

interface UsePrFileStatusChangesSubscriptionIfNeededProps {
  renderStatus: RenderStatusType | null
  presentationFile: PresentationFileFragment['presentationFile'] | undefined
  pageIdentifier: string | undefined
}

function usePrFileStatusChangesSubscriptionIfNeeded({
  renderStatus,
  presentationFile,
  pageIdentifier,
}: UsePrFileStatusChangesSubscriptionIfNeededProps) {
  const shouldSubscribeToPrFileStatusChanges =
    Boolean(pageIdentifier) &&
    renderStatus === 'FINISHED' &&
    presentationFile?.status === 'PENDING'

  usePresentationFileSubscription({
    variables: { pageId: pageIdentifier ?? '' },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const pageCacheId = dataIdFromObject({
        __typename: 'Page',
        identifier: pageIdentifier,
      })

      const newDownloadUrl = subscriptionData.data?.presentationFile.url

      if (pageCacheId && newDownloadUrl) {
        // Fragment contains more information than is returned from the
        // subscriptionData now, so we need to read it before re-writing it.
        const frag = client.readFragment<PresentationFileFragment>({
          fragment: PresentationFileFragmentDoc,
          id: pageCacheId,
          fragmentName: 'PresentationFile',
        })

        if (frag && frag.presentationFile) {
          client.writeFragment<PresentationFileFragment>({
            fragment: PresentationFileFragmentDoc,
            id: pageCacheId,
            data: {
              ...frag,
              presentationFile: {
                ...frag.presentationFile,
                downloadUrl: newDownloadUrl,
                status: 'COMPLETED',
              },
            },
          })
        }
      }
    },
    skip: !shouldSubscribeToPrFileStatusChanges,
  })
}
